import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, RefreshCw, LogIn, AlertCircle } from 'lucide-react';

const START_HOUR = 6;
const END_HOUR   = 21;
const HOUR_HEIGHT_PX = 56;

const pad = (n) => String(n).padStart(2, '0');

const toMinutes = (dateStr) => {
  const d = new Date(dateStr);
  return d.getHours() * 60 + d.getMinutes();
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${pad(m)} ${ampm}`;
};

const isoDateStr = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const EVENT_COLORS = [
  '#4f86f7', '#e06c75', '#56b6c2', '#98c379',
  '#e5c07b', '#c678dd', '#61afef', '#d19a66',
];
const colorForCalendar = (calId) => {
  let hash = 0;
  for (let i = 0; i < (calId || '').length; i++) hash = (hash * 31 + calId.charCodeAt(i)) & 0xffff;
  return EVENT_COLORS[hash % EVENT_COLORS.length];
};

export default function GoogleCalendarPanel({
  currentDate,
  gapiReady,
  googleSignedIn,
  onGoogleSignIn,
}) {
  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [expanded, setExpanded] = useState(null);
  const nowLineRef              = useRef(null);

  useEffect(() => {
    if (googleSignedIn && gapiReady) fetchEvents();
    if (!googleSignedIn) setEvents([]);
  }, [googleSignedIn, gapiReady, currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (nowLineRef.current) {
      nowLineRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [events]);

  const fetchEvents = async () => {
    setLoading(true);
    setError('');
    try {
      const dateStr = isoDateStr(currentDate);
      const timeMin = new Date(`${dateStr}T00:00:00`).toISOString();
      const timeMax = new Date(`${dateStr}T23:59:59`).toISOString();

      const response = await window.gapi.client.calendar.events.list({
        calendarId: 'primary',
        timeMin,
        timeMax,
        singleEvents: true,
        orderBy: 'startTime',
        maxResults: 50,
      });

      const items = response.result.items || [];
      setEvents(items.filter(e => e.start?.dateTime));
    } catch (e) {
      console.error('[OPP Calendar] fetch error:', e);
      setError('Could not load calendar events.');
    } finally {
      setLoading(false);
    }
  };

  const positionEvent = (event) => {
    const startMin     = toMinutes(event.start.dateTime);
    const endMin       = toMinutes(event.end.dateTime);
    const clampedStart = Math.max(startMin, START_HOUR * 60);
    const clampedEnd   = Math.min(endMin,   END_HOUR * 60);
    const top    = ((clampedStart - START_HOUR * 60) / 60) * HOUR_HEIGHT_PX;
    const height = Math.max(((clampedEnd - clampedStart) / 60) * HOUR_HEIGHT_PX, 20);
    return { top, height };
  };

  const getNowPosition = () => {
    if (isoDateStr(new Date()) !== isoDateStr(currentDate)) return null;
    const minutes = new Date().getHours() * 60 + new Date().getMinutes();
    if (minutes < START_HOUR * 60 || minutes > END_HOUR * 60) return null;
    return ((minutes - START_HOUR * 60) / 60) * HOUR_HEIGHT_PX;
  };

  const nowPosition = getNowPosition();
  const totalHeight = (END_HOUR - START_HOUR) * HOUR_HEIGHT_PX;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      backgroundColor: '#fff', borderRadius: '12px',
      border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      overflow: 'hidden', fontFamily: 'inherit',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px', borderBottom: '1px solid #e2e8f0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0, backgroundColor: 'var(--color-primary-light)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Calendar size={18} style={{ color: 'var(--color-primary)' }} />
          <span style={{ fontWeight: 700, fontSize: '15px', color: 'var(--color-primary)' }}>
            Google Calendar
          </span>
        </div>
        {googleSignedIn ? (
          <button onClick={fetchEvents} disabled={loading} title="Refresh events" style={{
            background: 'none', border: 'none', cursor: loading ? 'default' : 'pointer',
            padding: '4px', borderRadius: '6px', color: 'var(--color-primary)',
            opacity: loading ? 0.5 : 1, display: 'flex', alignItems: 'center',
          }}>
            <RefreshCw size={15} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
          </button>
        ) : (
          <button onClick={onGoogleSignIn} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '6px 12px', backgroundColor: 'var(--color-primary)',
            color: '#fff', border: 'none', borderRadius: '8px',
            fontSize: '13px', fontWeight: 600, cursor: 'pointer',
          }}>
            <LogIn size={13} /> Connect
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '10px 14px', backgroundColor: '#fff1f2',
          borderBottom: '1px solid #fecdd3',
          display: 'flex', alignItems: 'center', gap: '8px',
          fontSize: '13px', color: '#be123c', flexShrink: 0,
        }}>
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          {error}
        </div>
      )}

      {/* Not connected */}
      {!googleSignedIn && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '32px 20px', textAlign: 'center', gap: '12px',
        }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '50%',
            backgroundColor: 'var(--color-primary-light)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Calendar size={24} style={{ color: 'var(--color-primary)' }} />
          </div>
          <p style={{ margin: 0, fontWeight: 600, fontSize: '15px', color: '#1e293b' }}>
            Connect Google Calendar
          </p>
          <p style={{ margin: 0, fontSize: '13px', color: '#64748b', maxWidth: '200px', lineHeight: 1.5 }}>
            See your schedule alongside your tasks for the day.
          </p>
          <button onClick={onGoogleSignIn} style={{
            marginTop: '4px', padding: '9px 20px',
            backgroundColor: 'var(--color-primary)', color: '#fff',
            border: 'none', borderRadius: '9px',
            fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          }}>
            Connect Calendar
          </button>
        </div>
      )}

      {/* Loading */}
      {googleSignedIn && loading && (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexDirection: 'column',
          gap: '10px', color: '#94a3b8', fontSize: '14px',
        }}>
          <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-primary)' }} />
          Loading events...
        </div>
      )}

      {/* Timeline */}
      {googleSignedIn && !loading && (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ position: 'relative', height: `${totalHeight}px`, marginLeft: '48px' }}>
            {Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => {
              const hour = START_HOUR + i;
              const label = hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`;
              return (
                <div key={hour} style={{
                  position: 'absolute', top: `${i * HOUR_HEIGHT_PX}px`,
                  left: 0, right: 0, height: `${HOUR_HEIGHT_PX}px`,
                  borderTop: '1px solid #f1f5f9',
                }}>
                  <span style={{
                    position: 'absolute', left: '-44px', top: '-8px',
                    fontSize: '10px', fontWeight: 600, color: '#94a3b8',
                    width: '40px', textAlign: 'right', whiteSpace: 'nowrap',
                  }}>
                    {label}
                  </span>
                </div>
              );
            })}

            {nowPosition !== null && (
              <div ref={nowLineRef} style={{
                position: 'absolute', top: `${nowPosition}px`,
                left: 0, right: 0, height: '2px',
                backgroundColor: '#ef4444', zIndex: 10, pointerEvents: 'none',
              }}>
                <div style={{
                  position: 'absolute', left: '-6px', top: '-4px',
                  width: '10px', height: '10px',
                  borderRadius: '50%', backgroundColor: '#ef4444',
                }} />
              </div>
            )}

            {events.map((event) => {
              const { top, height } = positionEvent(event);
              const color = colorForCalendar(event.organizer?.email);
              const isExpanded = expanded === event.id;
              return (
                <div key={event.id} onClick={() => setExpanded(isExpanded ? null : event.id)} style={{
                  position: 'absolute', top: `${top}px`, left: '4px', right: '4px',
                  height: isExpanded ? 'auto' : `${height}px`,
                  minHeight: `${height}px`,
                  backgroundColor: color + '20', borderLeft: `3px solid ${color}`,
                  borderRadius: '6px', padding: '4px 7px',
                  cursor: 'pointer', overflow: 'hidden', zIndex: 5,
                  transition: 'box-shadow 0.15s ease',
                  boxShadow: isExpanded ? '0 4px 12px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                  <p style={{
                    margin: 0, fontSize: '12px', fontWeight: 700,
                    color: '#1e293b', lineHeight: 1.3, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap',
                  }}>
                    {event.summary || '(No title)'}
                  </p>
                  <p style={{
                    margin: '2px 0 0', fontSize: '10px', color: '#64748b',
                    display: 'flex', alignItems: 'center', gap: '3px',
                  }}>
                    <Clock size={9} />
                    {formatTime(event.start.dateTime)} – {formatTime(event.end.dateTime)}
                  </p>
                  {isExpanded && event.location && (
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#475569' }}>
                      📍 {event.location}
                    </p>
                  )}
                  {isExpanded && event.description && (
                    <p style={{
                      margin: '4px 0 0', fontSize: '11px', color: '#475569',
                      lineHeight: 1.4, borderTop: '1px solid #e2e8f0', paddingTop: '4px',
                    }}>
                      {event.description.replace(/<[^>]*>/g, '').slice(0, 200)}
                    </p>
                  )}
                </div>
              );
            })}

            {events.length === 0 && (
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center', color: '#cbd5e1',
              }}>
                <Calendar size={28} style={{ marginBottom: '8px' }} />
                <p style={{ margin: 0, fontSize: '13px', fontWeight: 500 }}>No events today</p>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
